import React from 'react';
import {
	EditButton,
	Datagrid,
	TextField,
	DateField,
	BooleanField,
	Filter,
	TextInput,
	SelectInput,
	ReferenceInput,
	NullableBooleanInput,
	DateInput
} from 'react-admin';
import TList from '../../components/wrapper/TList';
import PropTypes from 'prop-types';
import { toApiDateTimeFormat, toJavaScriptDateFormat } from '../../components/helper/DateHelper';
import {
	coreWalletLimitContextsForSelection,
	coreWalletLimitTypesForSelection,
	coreWalletLimitCurrencyTypesForSelection,
	coreWalletLimitSubtypesForSelection,
	coreWalletLimitScopesForSelection,
	coreWalletLimitPeriodsForSelection,
	coreWalletLimitOperationsForSelection
} from './LimitDefinitionConstants';
import { countriesForSelection } from '../../i18n/en.js';
import { coreWalletTypesForSelection } from '../wallets/WalletConstants';
import { coreWalletVerificationLevelsForSelection } from '../users/UserConstants';

const subsidiaryRenderer = choice => `${choice.name} (${choice.id})`;

const LimitDefinitionFilter = ({
	limitContextsForSelection,
	walletTypesForSelection,
	verificationLevelsForSelection,
	limitTypesForSelection,
	limitCurrencyTypesForSelection,
	limitSubtypesForSelection,
	limitScopesForSelection,
	limitPeriodsForSelection,
	limitOperationsForSelection,
	...props
}) => (
	<Filter {...props}>
		<ReferenceInput alwaysOn source="subsidiaryId" reference="subsidiaries">
			<SelectInput optionText={subsidiaryRenderer} resettable />
		</ReferenceInput>
		<TextInput alwaysOn resettable label="resources.limitDefinitions.filter.limitGroup" source="limitGroup" />
		<SelectInput
			alwaysOn
			resettable
			label="resources.limitDefinitions.filter.limitContext"
			source="limitContext"
			choices={limitContextsForSelection}
		/>
		<ReferenceInput
			alwaysOn
			source="limitCurrency"
			reference="currencies"
			label="resources.limitDefinitions.filter.limitCurrency"
		>
			<SelectInput optionText="currencyCode" resettable />
		</ReferenceInput>
		<SelectInput
			alwaysOn
			resettable
			choices={countriesForSelection}
			source="walletCountry"
			label="resources.limitDefinitions.filter.walletCountry"
		/>
		<SelectInput
			alwaysOn
			resettable
			choices={walletTypesForSelection}
			source="walletType"
			label="resources.limitDefinitions.filter.walletType"
		/>
		<SelectInput
			alwaysOn
			resettable
			choices={verificationLevelsForSelection}
			source="userVerificationLevel"
			label="resources.limitDefinitions.filter.userVerificationLevel"
		/>
		<NullableBooleanInput alwaysOn source="enabled" label="resources.limitDefinitions.filter.enabled" />
		<DateInput
			alwaysOn
			source="validFrom"
			label="resources.limitDefinitions.filter.validFrom"
			parse={value => toApiDateTimeFormat(value)}
			format={value => toJavaScriptDateFormat(value)}

		/>
		<DateInput
			alwaysOn
			source="validUntil"
			label="resources.limitDefinitions.filter.validUntil"
			parse={value => toApiDateTimeFormat(value)}
			format={value => toJavaScriptDateFormat(value)}
		/>
		<SelectInput
			alwaysOn
			resettable
			choices={limitTypesForSelection}
			source="limitType"
			label="resources.limitDefinitions.filter.limitType"
		/>
		<SelectInput
			alwaysOn
			resettable
			choices={limitCurrencyTypesForSelection}
			source="limitCurrencyType"
			label="resources.limitDefinitions.filter.limitCurrencyType"
		/>
		<SelectInput
			alwaysOn
			resettable
			choices={limitSubtypesForSelection}
			source="limitSubtype"
			label="resources.limitDefinitions.filter.limitSubtype"
		/>
		<SelectInput
			alwaysOn
			resettable
			choices={limitScopesForSelection}
			source="limitScope"
			label="resources.limitDefinitions.filter.limitScope"
		/>
		<TextInput
			alwaysOn
			resettable
			source="limitPaymentMethod"
			label="resources.limitDefinitions.filter.limitPaymentMethod"
		/>
		<SelectInput
			alwaysOn
			resettable
			choices={limitPeriodsForSelection}
			source="limitPeriod"
			label="resources.limitDefinitions.filter.limitPeriod"
		/>
		<SelectInput
			alwaysOn
			resettable
			choices={limitOperationsForSelection}
			source="limitOperation"
			label="resources.limitDefinitions.filter.limitOperation"
		/>
		<TextInput
			alwaysOn
			resettable
			source="limitPeriodAmount"
			label="resources.limitDefinitions.filter.limitPeriodAmount"
		/>
		<TextInput alwaysOn resettable source="customType" label="resources.limitDefinitions.filter.customType" />
	</Filter>
);

const LimitDefinitionList = ({
	limitContextsForSelection,
	walletTypesForSelection,
	verificationLevelsForSelection,
	limitTypesForSelection,
	limitCurrencyTypesForSelection,
	limitSubtypesForSelection,
	limitScopesForSelection,
	limitPeriodsForSelection,
	limitOperationsForSelection,
	...props
}) => (
	<TList
		{...props}
		exporter={false}
		filter={{ walletId: '' }}
		filters={
			<LimitDefinitionFilter
				limitContextsForSelection={limitContextsForSelection}
				walletTypesForSelection={walletTypesForSelection}
				verificationLevelsForSelection={verificationLevelsForSelection}
				limitTypesForSelection={limitTypesForSelection}
				limitCurrencyTypesForSelection={limitCurrencyTypesForSelection}
				limitSubtypesForSelection={limitSubtypesForSelection}
				limitScopesForSelection={limitScopesForSelection}
				limitPeriodsForSelection={limitPeriodsForSelection}
				limitOperationsForSelection={limitOperationsForSelection}
			/>
		}
	>
		<Datagrid>
			<EditButton />
			<TextField source="id" sortable={false} />
			<TextField source="subsidiaryId" sortable={false} />
			<TextField source="limitGroupId" sortable={false} />
			<DateField source="creationDateTime" showTime sortable={false} />
			<TextField source="customType" sortable={false} />
			<BooleanField source="enabled" sortable={false} />
			<TextField source="limitType" sortable={false} />
			<TextField source="limitSubtype" sortable={false} />
			<TextField source="limitScope" sortable={false} />
			<TextField source="limitContext" sortable={false} />
			<TextField source="limitCurrency" sortable={false} />
			<TextField source="limitCurrencyType" sortable={false} />
			<TextField source="limitPeriod" sortable={false} />
			<TextField source="walletCountry" sortable={false} />
			<TextField source="walletType" sortable={false} />
			<TextField source="userVerificationLevel" sortable={false} />
			<TextField source="limitPaymentMethod" sortable={false} />
			<DateField source="validFrom" showTime sortable={false} />
			<DateField source="validUntil" showTime sortable={false} />
		</Datagrid>
	</TList>
);

LimitDefinitionList.propTypes = {
	limitContextsForSelection: PropTypes.array.isRequired,
	walletTypesForSelection: PropTypes.array.isRequired,
	verificationLevelsForSelection: PropTypes.array.isRequired,
	limitTypesForSelection: PropTypes.array.isRequired,
	limitCurrencyTypesForSelection: PropTypes.array.isRequired,
	limitSubtypesForSelection: PropTypes.array.isRequired,
	limitScopesForSelection: PropTypes.array.isRequired,
	limitPeriodsForSelection: PropTypes.array.isRequired,
	limitOperationsForSelection: PropTypes.array.isRequired
};
LimitDefinitionList.defaultProps = {
	limitContextsForSelection: coreWalletLimitContextsForSelection,
	walletTypesForSelection: coreWalletTypesForSelection,
	verificationLevelsForSelection: coreWalletVerificationLevelsForSelection,
	limitTypesForSelection: coreWalletLimitTypesForSelection,
	limitCurrencyTypesForSelection: coreWalletLimitCurrencyTypesForSelection,
	limitSubtypesForSelection: coreWalletLimitSubtypesForSelection,
	limitScopesForSelection: coreWalletLimitScopesForSelection,
	limitPeriodsForSelection: coreWalletLimitPeriodsForSelection,
	limitOperationsForSelection: coreWalletLimitOperationsForSelection
};

export default LimitDefinitionList;
